import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/hooks/use-toast';
import { Save, Navigation, RotateCcw } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface SOSReport {
  id: string;
  user_id: string;
  type: 'lost' | 'found' | 'emergency' | 'danger';
  title: string;
  description: string | null;
  animal_species: string | null;
  animal_breed: string | null;
  animal_name: string | null;
  image_url: string | null;
  latitude: number;
  longitude: number;
  location_precision: 'precise' | 'street' | 'approximate';
  status: 'open' | 'spotted' | 'in_progress' | 'resolved' | 'cancelled';
  contact_phone: string | null;
  allow_public_sharing: boolean;
}

interface EditSOSModalProps {
  sos: SOSReport | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

type LocationPrecision = 'precise' | 'street' | 'approximate';

export const EditSOSModal = ({ sos, open, onOpenChange, onSuccess }: EditSOSModalProps) => {
  const [loading, setLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const marker = useRef<L.Marker | null>(null);
  
  // Form data
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [animalSpecies, setAnimalSpecies] = useState('');
  const [animalBreed, setAnimalBreed] = useState('');
  const [animalName, setAnimalName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationPrecision, setLocationPrecision] = useState<LocationPrecision>('approximate');
  const [contactPhone, setContactPhone] = useState('');
  const [allowPublicSharing, setAllowPublicSharing] = useState(true);

  useEffect(() => {
    if (sos && open) {
      setTitle(sos.title);
      setDescription(sos.description || '');
      setAnimalSpecies(sos.animal_species || '');
      setAnimalBreed(sos.animal_breed || '');
      setAnimalName(sos.animal_name || '');
      setImageUrl(sos.image_url || '');
      setLocation({ lat: sos.latitude, lng: sos.longitude });
      setLocationPrecision(sos.location_precision);
      setContactPhone(sos.contact_phone || '');
      setAllowPublicSharing(sos.allow_public_sharing);
      
      // Initialize map after setting location
      setTimeout(() => {
        initializeMap();
      }, 100);
    }
  }, [sos, open]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast({
        title: "Geolocalizzazione non disponibile",
        description: "Il tuo browser non supporta la geolocalizzazione",
        variant: "destructive"
      });
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setLocation(newLocation);
        updateMapLocation(newLocation);
        setGettingLocation(false);
        toast({
          title: "Posizione aggiornata",
          description: "La tua posizione è stata rilevata automaticamente"
        });
      },
      (error) => {
        setGettingLocation(false);
        console.error('Error getting location:', error);
        toast({
          title: "Errore posizione",
          description: "Impossibile rilevare la posizione. Seleziona manualmente sulla mappa.",
          variant: "destructive"
        });
      }
    );
  };

  const updateMapLocation = (newLocation: { lat: number; lng: number }) => {
    if (map.current && marker.current) {
      marker.current.setLatLng([newLocation.lat, newLocation.lng]);
      map.current.setView([newLocation.lat, newLocation.lng], 16);
    }
  };

  const initializeMap = () => {
    if (!mapContainer.current || map.current || !location) return;
    
    map.current = L.map(mapContainer.current, {
      center: [location.lat, location.lng],
      zoom: 16,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map.current);

    // Add draggable marker
    marker.current = L.marker([location.lat, location.lng], {
      draggable: true,
      icon: L.divIcon({
        html: `
          <div style="
            background-color: #dc2626;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            border: 3px solid white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            cursor: move;
          ">
            📍
          </div>
        `,
        className: 'custom-sos-marker',
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      })
    }).addTo(map.current);

    marker.current.bindPopup('Trascina per cambiare posizione');

    // Update location when marker is dragged
    marker.current.on('dragend', (e) => {
      const newLatLng = e.target.getLatLng();
      setLocation({
        lat: newLatLng.lat,
        lng: newLatLng.lng
      });
    });

    // Add click handler to map
    map.current.on('click', (e) => {
      const newLocation = {
        lat: e.latlng.lat,
        lng: e.latlng.lng
      };
      setLocation(newLocation);
      if (marker.current) {
        marker.current.setLatLng([newLocation.lat, newLocation.lng]);
      }
    });
  };

  const cleanupMap = () => {
    if (map.current) {
      map.current.remove();
      map.current = null;
      marker.current = null;
    }
  };

  useEffect(() => {
    return () => {
      cleanupMap();
    };
  }, []);

  const handleSubmit = async () => {
    if (!sos || !location) return;

    try {
      setLoading(true);

      const { error } = await supabase
        .from('sos_reports')
        .update({
          title,
          description: description || null,
          animal_species: animalSpecies || null,
          animal_breed: animalBreed || null,
          animal_name: animalName || null,
          image_url: imageUrl || null,
          latitude: location.lat,
          longitude: location.lng,
          location_precision: locationPrecision,
          contact_phone: contactPhone || null,
          allow_public_sharing: allowPublicSharing,
          updated_at: new Date().toISOString()
        })
        .eq('id', sos.id);

      if (error) throw error;

      toast({
        title: "SOS aggiornato!",
        description: "Le modifiche sono state salvate con successo.",
      });

      cleanupMap();
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Error updating SOS:', error);
      toast({
        title: "Errore",
        description: "Impossibile aggiornare il SOS. Riprova.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!sos) return null;

  return (
    <Dialog open={open} onOpenChange={(open) => {
      if (!open) {
        cleanupMap();
      }
      onOpenChange(open);
    }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifica SOS</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Titolo *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="es. Cane smarrito zona Parco Centrale"
                maxLength={100}
              />
            </div>

            <div>
              <Label htmlFor="species">Animale (specie) *</Label>
              <Input
                id="species"
                value={animalSpecies}
                onChange={(e) => setAnimalSpecies(e.target.value)}
                placeholder="es. Cane, Gatto, Coniglio..."
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="breed">Razza</Label>
                <Input
                  id="breed"
                  value={animalBreed}
                  onChange={(e) => setAnimalBreed(e.target.value)}
                  placeholder="es. Labrador"
                />
              </div>
              <div>
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  value={animalName}
                  onChange={(e) => setAnimalName(e.target.value)}
                  placeholder="Se conosciuto"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Cosa è successo, segni particolari, comportamento..."
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="image">Foto (URL)</Label>
              <Input
                id="image"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://esempio.com/foto.jpg"
              />
            </div>

            <div>
              <Label>Aggiorna posizione</Label>
              <div className="space-y-3 mt-2">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={getCurrentLocation}
                    disabled={gettingLocation}
                    className="flex-1"
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    {gettingLocation ? 'Rilevando...' : 'Usa posizione attuale'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (sos) {
                        const originalLocation = { lat: sos.latitude, lng: sos.longitude };
                        setLocation(originalLocation);
                        updateMapLocation(originalLocation);
                      }
                    }}
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>
                
                <div 
                  ref={mapContainer} 
                  className="w-full h-64 rounded-lg border border-border"
                />
                
                <p className="text-xs text-muted-foreground">
                  Clicca sulla mappa o trascina il marker per aggiornare la posizione
                </p>
              </div>
            </div>

            <div>
              <Label>Precisione posizione</Label>
              <RadioGroup 
                value={locationPrecision} 
                onValueChange={(value) => setLocationPrecision(value as LocationPrecision)}
                className="mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="precise" id="precise" />
                  <Label htmlFor="precise">📍 Precisa (entro 10m)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="street" id="street" />
                  <Label htmlFor="street">🛣️ Via/zona (entro 100m)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="approximate" id="approximate" />
                  <Label htmlFor="approximate">🏘️ Area approssimativa (entro 1km)</Label>
                </div>
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="phone">Telefono di contatto</Label>
              <Input
                id="phone"
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="+39 123 456 7890"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="sharing"
                checked={allowPublicSharing}
                onCheckedChange={setAllowPublicSharing}
              />
              <div className="grid gap-1.5 leading-none">
                <Label htmlFor="sharing" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Consenti condivisione pubblica
                </Label>
                <p className="text-xs text-muted-foreground">
                  Permetti ad altri di condividere questo SOS sui social media
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <Button onClick={handleSubmit} disabled={loading || !location} className="flex-1">
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Salvando...' : 'Salva Modifiche'}
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};